import React, { useEffect, useMemo, useState } from 'react';

/* External */
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

/* Material UI */
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import ButtonWithLoader from 'commons/containers/ButtonWithLoader';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

/* Project */
import DatePicker from 'commons/components/DatePicker';
import { getUnitDetails, truthty } from 'utils/functions';
import WeightField from 'commons/containers/WeightField';
import { parseToDate } from 'utils/DateFormat';
import { useAutomation } from 'utils/hooks/useAutomationPermission';

function LotForm(props) {
  const {
    controls: propsControls,
    changeControls,
    changeControlsNeedsEvent,
    invalidControls,
    warehouses,
    fruits,
    varieties,
    products,
    objects,
    packagingTypes,
    changeWeight,
    totalWeight,
    toggleForm,
    submit,
    isOpen,
    entryLots,
    process,
    actions,
    module,
  } = props;

  const { t } = useTranslation();
  const { hasAutomation } = useAutomation();

  const settings = useSelector((state) => state.app.account);
  const { enableProcessDateInLots } = settings;

  const [controls, setControls] = useState({ ...propsControls });
  useEffect(() => {
    setControls({ ...propsControls });
  }, [propsControls]);

  useEffect(() => {
    if (controls.inboundLotId) {
      const lot = entryLots.find((entryLot) => entryLot.id === controls.inboundLotId);
      const originProcess = typeof lot?.originProcess === 'function' ? lot.originProcess() : null;
      let receipt;

      if (originProcess?.receipt && typeof originProcess.receipt === 'function') {
        receipt = originProcess.receipt();
      }

      const processDate = receipt?.harvestDate || lot?.processDate || originProcess?.date;
      if (processDate) {
        actions.changeControls(module, { processDate });
      }
    }
  }, [controls.inboundLotId]);

  const { unitCode, unitName } = getUnitDetails(objects.accounts);

  const availableFruitIds = entryLots.map((lot) => lot.fruitId);
  const fruitOptions = Object.values(fruits).filter((fruit) =>
    availableFruitIds.includes(fruit.id),
  );
  const varietyOptions = Object.values(varieties).filter(
    (variety) => variety.fruitId === controls.fruitId,
  );

  const getVarietiesById = (varietyId) => {
    const variety = Object.values(varieties).find((varietyIt) => varietyIt.id === varietyId);
    return variety || null;
  };

  const productsOptions = useMemo(
    () => ['', ...Object.values(products).sort((a, b) => a.name - b.name)],
    [products],
  );

  const productsOptionsFiltered = controls.varietyId
    ? productsOptions.filter((product) => product.varietyId === controls.varietyId)
    : productsOptions.filter((product) =>
      varietyOptions.find((variety) => product.varietyId === variety.id),
    );

  const productsOptionsWithoutVariety = productsOptions.filter((product) => !product.varietyId);

  productsOptionsWithoutVariety.forEach((product) => productsOptionsFiltered.push(product));

  const getLotProductName = (lot) => {
    const relatedProduct =
      (typeof lot?.product === 'function' && lot.product()) ||
      objects?.products?.[lot?.productId];
    return relatedProduct?.name || '';
  };

  const datetimePicker = (
    <DatePicker
      changeControlsNeedsEvent={changeControlsNeedsEvent}
      inputId="datetime"
      label={t('process.entryDate')}
      fullWidth
      value={controls.datetime}
      onChange={changeControls}
      type="datetime-local"
      inputProps={{
        'data-module': 'departure',
        min: parseToDate(process.date).date,
      }}
    />
  );

  const warehouseSelect = (
    <FormControl fullWidth>
      <InputLabel id="warehouse-label">{t('Warehouse')}</InputLabel>
      <Select
        labelId="warehouse-label"
        fullWidth
        value={controls.warehouseId}
        disabled={controls.lotId}
        onChange={changeControls}
        error={invalidControls.warehouseId}>
        <MenuItem id="warehouseId" value="" data-module="departure">
          {t('None')}
        </MenuItem>
        {Object.values(warehouses).map((warehouse) => (
          <MenuItem
            key={`warehouse-option-${warehouse.id}`}
            id="warehouseId"
            value={warehouse.id}
            data-module="departure">
            {warehouse.name}
          </MenuItem>
        ))}
      </Select>
      {truthty(invalidControls.warehouseId) && (
        <FormHelperText error>{t('process.selectWarehouseError')}</FormHelperText>
      )}
    </FormControl>
  );

  const fruitSelect = (
    <Autocomplete
      value={controls.fruitId && fruits[controls.fruitId]}
      id="autocomplete-fruit-process"
      options={fruitOptions.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))}
      getOptionLabel={(fruit) => fruit && fruit.name}
      renderInput={(params) => (
        <TextField style={{ margin: 0 }} {...params} label={t('Species')} margin="normal" />
      )}
      disabled={controls.lotId}
      onChange={(event, newValue) => {
        const e = {
          autocomplete: true,
          input: 'fruitId',
          value: newValue ? newValue.id : '',
          module: 'departure',
        };
        changeControls(e);
      }}
    />
  );

  const varietySelect = (
    <Autocomplete
      value={controls.varietyId && varieties[controls.varietyId]}
      id="autocomplete-variety-process"
      options={varietyOptions.sort((a, b) =>
        a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
      )}
      getOptionLabel={(variety) => variety && variety.name}
      renderInput={(params) => (
        <TextField style={{ margin: 0 }} {...params} label={t('Variety')} margin="normal" />
      )}
      disabled={!controls.fruitId || controls.lotId}
      onChange={(event, newValue) => {
        const e = {
          autocomplete: true,
          input: 'varietyId',
          value: newValue ? newValue.id : '',
          module: 'departure',
        };
        changeControls(e);
      }}
    />
  );

  const productSelect = (
    <Autocomplete
      value={
        controls.productId &&
        productsOptionsFiltered.find((product) => product.id === controls.productId)
      }
      id="autocomplete-product-process"
      options={productsOptionsFiltered}
      getOptionLabel={(product) => product && product.name}
      renderInput={(params) => (
        <TextField style={{ margin: 0 }} {...params} label={t('Product')} margin="normal" />
      )}
      disabled={!controls.varietyId || controls.lotId}
      onChange={(event, newValue) => {
        const e = {
          autocomplete: true,
          input: 'productId',
          value: newValue ? newValue.id : '',
          module: 'departure',
        };
        changeControls(e);
      }}
    />
  );

  const packagingSelect = (
    <>
      <Autocomplete
        value={
          controls.packagingTypeId &&
          Object.values(packagingTypes).find(
            (packagingType) => packagingType.id === controls.packagingTypeId,
          )
        }
        id="autocomplete-packagingType-receipts"
        options={Object.values(packagingTypes).sort((a, b) =>
          a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
        )}
        getOptionLabel={(packagingType) => packagingType && packagingType.name}
        renderInput={(params) => (
          <TextField
            style={{ margin: 0 }}
            {...params}
            label={t('process.packagingType')}
            margin="normal"
          />
        )}
        disabled={controls.lotId}
        onChange={(event, newValue) => {
          const e = {
            autocomplete: true,
            input: 'packagingTypeId',
            value: newValue ? newValue.id : '',
            module: 'departure',
          };
          changeControls(e);
        }}
      />
      {truthty(invalidControls.packagingTypeId) && (
        <FormHelperText error>{t('process.selectPackagingTypeError')}</FormHelperText>
      )}
    </>
  );

  const weightInput = (
    <WeightField
      // @ts-ignore
      quantity={controls.quantity}
      changeWeight={changeWeight}
      changeControls={changeControls}
      error={invalidControls.quantity}
      inputProps={{ 'data-module': 'departure' }}
      helperText={invalidControls.quantity && t('process.enterQuantityError', { unit: unitName })}
      totalWeight={totalWeight}
      label={unitCode}
    />
  );

  const packagingQuantityInput = (
    <TextField
      value={controls.packagingQuantity}
      disabled={controls.lotId}
      onChange={changeControls}
      fullWidth
      id="packagingQuantity"
      label={t('process.packagingQuantity')}
      inputProps={{ 'data-module': 'departure' }}
      error={invalidControls.packagingQuantity}
      helperText={invalidControls.packagingQuantity && t('process.enterPackagingQuantityError')}
    />
  );

  const copyQuantityInput = (
    <TextField
      value={controls.copyQuantity}
      onChange={changeControls}
      fullWidth
      id="copyQuantity"
      label={t('process.copyQuantity')}
      inputProps={{ 'data-module': 'departure' }}
      error={invalidControls.copyQuantity}
      helperText={invalidControls.copyQuantity && t('process.enterCopyQuantityError')}
    />
  );

  const inboundLotSelect = (
    <FormControl fullWidth>
      <InputLabel id="inboud-label">{t('process.inboundLot')}</InputLabel>
      <Select
        disabled={controls.lotId}
        labelId="inboud-lot-label"
        fullWidth
        value={controls.inboundLotId}
        onChange={changeControls}
        error={invalidControls.inboundLotId}>
        <MenuItem id="inboundLotId" value="" data-module="departure">
          {t('None')}
        </MenuItem>
        {Object.values(entryLots).map((lot) => (
          <MenuItem
            key={`inbound-option-${lot.id}`}
            id="inboundLotId"
            value={lot.id}
            data-module="departure">
            {lot.id} - {getLotProductName(lot)}
          </MenuItem>
        ))}
      </Select>
      {truthty(invalidControls.warehouseId) && (
        <FormHelperText error>{t('process.selectWarehouseError')}</FormHelperText>
      )}
    </FormControl>
  );

  const customField = (
    <TextField
      disabled={controls.lotId}
      value={controls.customField}
      onChange={changeControls}
      fullWidth
      id="customField"
      label={t('process.customField')}
      inputProps={{ 'data-module': 'departure' }}
    />
  );

  const processDate = (
    <DatePicker
      disabled={controls.lotId}
      inputId="processDate"
      label={t('process.processDate')}
      fullWidth
      value={controls.processDate}
      onChange={changeControls}
      type="date"
      inputProps={{
        'data-module': 'departure',
      }}
    />
  );

  const dialogContent = (
    <DialogContent>
      <Grid container spacing={2}>
        <Grid item sm={6} xs={12}>
          {datetimePicker}
        </Grid>

        <Grid item sm={6} xs={12}>
          {warehouseSelect}
        </Grid>

        <Grid item sm={6} xs={12}>
          {inboundLotSelect}
        </Grid>

        <Grid item sm={6} xs={12}>
          {customField}
        </Grid>

        {enableProcessDateInLots && (
          <Grid item sm={6} xs={12}>
            {processDate}
          </Grid>
        )}

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <Typography gutterBottom variant="h6" component="h6">
            {t('Product')}
          </Typography>
          <div>
            <Autocomplete
              value=""
              id="autocomplete-product-code"
              options={productsOptions.filter((product) => product.code)}
              getOptionLabel={(product) => product && product.code}
              renderInput={(params) => (
                <TextField style={{ margin: 0 }} {...params} label={t('Code')} margin="normal" />
              )}
              onChange={(event, newValue) => {
                if (newValue && newValue?.varietyId) {
                  const selectedVariety = getVarietiesById(newValue.varietyId);
                  changeControls({
                    autocomplete: true,
                    input: 'fruitId',
                    value: selectedVariety ? selectedVariety.fruitId : '',
                    module: 'departure',
                  });
                  changeControls({
                    autocomplete: true,
                    input: 'varietyId',
                    value: selectedVariety ? selectedVariety.id : '',
                    module: 'departure',
                  });
                  changeControls({
                    autocomplete: true,
                    input: 'productId',
                    value: newValue ? newValue.id : '',
                    module: 'departure',
                  });
                } else if (newValue && newValue?.id) {
                  changeControls({
                    autocomplete: true,
                    input: 'productId',
                    value: newValue ? newValue.id : '',
                    module: 'departure',
                  });
                }
              }}
            />
          </div>
        </Grid>

        <Grid item sm={6} xs={12}>
          {fruitSelect}
        </Grid>

        <Grid item sm={6} xs={12}>
          {varietySelect}
        </Grid>

        <Grid item sm={6} xs={12}>
          {productSelect}
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <Typography gutterBottom variant="h6" component="h6">
            {t('process.packaging')}
          </Typography>
        </Grid>

        <Grid item sm={6} xs={12}>
          {packagingSelect}
        </Grid>

        <Grid item sm={6} xs={12}>
          {weightInput}
        </Grid>

        <Grid item sm={6} xs={12}>
          {packagingQuantityInput}
        </Grid>

        {controls.lotId && (
          <Grid item sm={6} xs={12}>
            {copyQuantityInput}
          </Grid>
        )}
      </Grid>
    </DialogContent>
  );

  const disableAddButton =
    truthty(invalidControls.warehouseId) ||
    truthty(invalidControls.fruitId) ||
    truthty(invalidControls.varietyId) ||
    truthty(invalidControls.productId) ||
    truthty(invalidControls.packagingTypeId) ||
    truthty(invalidControls.quantity) ||
    truthty(invalidControls.packagingQuantity) ||
    totalWeight <= 0;

  const dialogActions = (
    <DialogActions>
      <Button onClick={toggleForm} data-module="departure" color="primary" variant="outlined">
        {t('Cancel')}
      </Button>
      <ButtonWithLoader
        onClick={submit}
        disabled={disableAddButton}
        autoFocus
        buttonId="add-departure-lot">
        {t('process.add')}
      </ButtonWithLoader>
    </DialogActions>
  );

  return (
    <Dialog
      TransitionProps={{ 'data-module': 'departure' }}
      aria-labelledby="simple-dialog-title"
      fullWidth
      onClose={toggleForm}
      open={isOpen}>
      <DialogTitle>
        {controls.lotId ? t('process.createMultipleCopiesTitle') : t('process.addOutputPakTitle')}
      </DialogTitle>

      {dialogContent}
      {dialogActions}
    </Dialog>
  );
}

export default LotForm;
